import { type ReactNode, useRef } from 'react';

import FocusTrap from 'focus-trap-react';
import { useTranslation } from 'react-i18next';

import Animate from '~/components/Animate';
import IconButton from '~/components/Button/IconButton';
import DrawerContainer from '~/components/Drawer';
import { Icons } from '~/components/Icon/constants';
import Overlay from '~/components/Overlay';
import Portal from '~/components/Portal';
import useBodyScrollLock from '~/hooks/use-body-scroll-lock';
import { useBreakpoints } from '~/hooks/use-breakpoints';

import styles from './styles';

interface IModalProps {
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
}

const Modal = ({ children, isOpen, onClose }: IModalProps): JSX.Element => {
  const { t } = useTranslation();
  const { lessThan } = useBreakpoints();
  const modalRef = useRef<HTMLDivElement>(null);

  useBodyScrollLock(isOpen, modalRef.current);

  return (
    <Portal>
      {lessThan.LG ? (
        <DrawerContainer
          isOpen={isOpen}
          onClose={onClose}
          renderDrawerHeader={(handleClose) => (
            <IconButton
              buttonCss={styles.closeButton}
              icon={Icons.Close}
              ariaLabel={t('modal.close')}
              iconSizeInPx={16}
              onClick={handleClose}
              css={styles.closeIcon}
            />
          )}
        >
          {children}
        </DrawerContainer>
      ) : (
        <div ref={modalRef}>
          <Animate.FadeInOut isVisible={isOpen} motionCss={styles.container}>
            <FocusTrap>
              <dialog css={styles.inner}>
                <Overlay onClick={onClose} />
                <div css={styles.modal}>
                  <IconButton
                    buttonCss={styles.closeButton}
                    icon={Icons.Close}
                    ariaLabel={t('modal.close')}
                    iconSizeInPx={16}
                    onClick={onClose}
                    css={styles.closeIcon}
                  />
                  <div css={styles.modalContent}>{children}</div>
                </div>
              </dialog>
            </FocusTrap>
          </Animate.FadeInOut>
        </div>
      )}
    </Portal>
  );
};

export default Modal;
